/**
 * CategoryPicker — a searchable, hierarchical category combobox (FIN-16).
 *
 * Behaviour mirrors FinEasy's ComboBoxCategorySelector, reimplemented in the
 * K-OS design system (CSS Modules + @k-os/ui tokens, no Tailwind, no lucide):
 *
 *   - Closed: the input shows the selected category's full path
 *     (e.g. "Rendimentos → Salário → Ordenado JC").
 *   - Focus clears the text and opens the dropdown.
 *   - Typing filters the tree IN PLACE: branches without a matching leaf are
 *     pruned, but the hierarchy stays (parents remain indented headers, each
 *     shown once) so distinct leaves under the same parent never look alike.
 *     A leaf matches when its full path contains the query (case-insensitive),
 *     so typing a parent name still reveals its children.
 *   - Empty query shows the full hierarchical tree: parents are non-selectable
 *     indented headers; only active leaves are selectable. The selected leaf
 *     shows a check.
 *   - The dropdown grows to fit the longest visible name (bounded by the input
 *     width below and the viewport edge above), so nothing is truncated.
 *   - Keyboard: ArrowUp/Down move the highlight, Enter selects, Escape closes.
 *     The highlighted item scrolls into view.
 *   - The dropdown is rendered in a portal positioned under the input (via
 *     getBoundingClientRect, repositioned on scroll/resize) so it is never
 *     clipped inside a table row or a modal, and its z-index sits above the
 *     modal overlay.
 *
 * `kind` filtering decision: when `kind` is provided we FILTER OUT leaves of
 * the other kind (and any parent left with no selectable descendant). This is
 * cleaner than muting and prevents the API's `category_kind_mismatch` error,
 * since only valid targets are ever offered.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Icon } from '@k-os/ui';
import type { FinanceCategoryDto } from '../../api/finance';
import type { FinanceCategoryKind } from '@k-os/core';
import styles from './CategoryPicker.module.css';

export interface CategoryPickerProps {
  /** Raw flat category list (income + expense, active flags intact). */
  categories: FinanceCategoryDto[];
  /** Currently selected categoryId, or null when uncategorized. */
  value: string | null;
  onChange: (categoryId: string) => void;
  /** Restrict SELECTABLE leaves to this kind (filters out the other kind). */
  kind?: FinanceCategoryKind;
  placeholder?: string;
  ariaLabel?: string;
  disabled?: boolean;
  /** Compact sizing for dense inline use (e.g. a table row). */
  compact?: boolean;
}

interface TreeNode {
  cat: FinanceCategoryDto;
  depth: number;
  children: TreeNode[];
}

/**
 * Fold a string for accent-insensitive search: decompose accented characters
 * (NFD) and strip the combining marks, then lowercase. So "cole" matches
 * "Colégio", "acai" matches "Açaí", etc.
 */
function fold(s: string): string {
  // Strip combining diacritical marks after NFD decomposition (é→e, ç→c, ã→a).
  return s.normalize('NFD').replace(/\p{Diacritic}/gu, '').toLowerCase();
}

let portalIdSeq = 0;

export function CategoryPicker({
  categories,
  value,
  onChange,
  kind,
  placeholder = 'Select a category…',
  ariaLabel,
  disabled = false,
  compact = false,
}: CategoryPickerProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [highlight, setHighlight] = useState(-1);
  const [position, setPosition] = useState<{ top: number; left: number; width: number } | null>(
    null,
  );

  const wrapRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const listboxId = useRef(`category-picker-${++portalIdSeq}`);

  const byId = useMemo(() => new Map(categories.map((c) => [c.id, c])), [categories]);

  const buildPath = useMemo(() => {
    return (categoryId: string | null): string => {
      if (!categoryId) return '';
      const cat = byId.get(categoryId);
      if (!cat) return '';
      const parts = [cat.name];
      let current = cat;
      while (current.parentId && byId.has(current.parentId)) {
        current = byId.get(current.parentId)!;
        parts.unshift(current.name);
      }
      return parts.join(' → ');
    };
  }, [byId]);

  const isLeaf = useMemo(() => {
    const parents = new Set<string>();
    for (const c of categories) if (c.parentId) parents.add(c.parentId);
    return (categoryId: string) => !parents.has(categoryId);
  }, [categories]);

  /** A leaf is selectable when it's a true leaf, active, and matches `kind`. */
  const isSelectable = useMemo(() => {
    return (cat: FinanceCategoryDto): boolean =>
      isLeaf(cat.id) && cat.active && (kind === undefined || cat.kind === kind);
  }, [isLeaf, kind]);

  /**
   * Hierarchical tree, pruned so a parent is dropped when it has no selectable
   * descendant. Roots: income before expense, then alphabetical within a level.
   */
  const tree = useMemo<TreeNode[]>(() => {
    const nodeById = new Map<string, TreeNode>(
      categories.map((c) => [c.id, { cat: c, depth: 0, children: [] }]),
    );
    const roots: TreeNode[] = [];
    for (const node of nodeById.values()) {
      const parent = node.cat.parentId ? nodeById.get(node.cat.parentId) : undefined;
      if (parent) parent.children.push(node);
      else roots.push(node);
    }

    const assignDepth = (nodes: TreeNode[], depth: number) => {
      for (const n of nodes) {
        n.depth = depth;
        assignDepth(n.children, depth + 1);
      }
    };
    assignDepth(roots, 0);

    // Prune: keep a leaf only if selectable; keep a parent only if it has a kept
    // descendant.
    const prune = (nodes: TreeNode[]): TreeNode[] => {
      const out: TreeNode[] = [];
      for (const n of nodes) {
        if (n.children.length === 0) {
          if (isSelectable(n.cat)) out.push(n);
        } else {
          const keptChildren = prune(n.children);
          if (keptChildren.length > 0) out.push({ ...n, children: keptChildren });
        }
      }
      return out;
    };

    const sortRec = (nodes: TreeNode[]) => {
      nodes.sort((a, b) => {
        if (a.depth === 0 && a.cat.kind !== b.cat.kind) {
          return a.cat.kind === 'income' ? -1 : 1;
        }
        return a.cat.name.localeCompare(b.cat.name);
      });
      for (const n of nodes) sortRec(n.children);
    };

    const pruned = prune(roots);
    sortRec(pruned);
    return pruned;
  }, [categories, isSelectable]);

  /**
   * The tree actually shown. When filtering we prune `tree` to the branches
   * that contain a matching leaf — keeping the hierarchy intact (parents stay
   * as headers, each shown once) rather than flattening to repeated breadcrumb
   * strings. A leaf matches when its full path contains the query, so typing a
   * parent name still reveals its children.
   */
  const visibleTree = useMemo<TreeNode[]>(() => {
    if (!query) return tree;
    const q = fold(query);
    const filterRec = (nodes: TreeNode[]): TreeNode[] => {
      const out: TreeNode[] = [];
      for (const n of nodes) {
        if (n.children.length === 0) {
          if (fold(buildPath(n.cat.id)).includes(q)) out.push(n);
        } else {
          const kept = filterRec(n.children);
          if (kept.length > 0) out.push({ ...n, children: kept });
        }
      }
      return out;
    };
    return filterRec(tree);
  }, [query, tree, buildPath]);

  /** Selectable leaf ids in render order — maps the keyboard highlight. */
  const selectableIds = useMemo<string[]>(() => {
    const ids: string[] = [];
    const walk = (nodes: TreeNode[]) => {
      for (const n of nodes) {
        if (n.children.length === 0) ids.push(n.cat.id);
        else walk(n.children);
      }
    };
    walk(visibleTree);
    return ids;
  }, [visibleTree]);

  const selectedPath = useMemo(() => buildPath(value), [buildPath, value]);

  // ─── Portal positioning ──────────────────────────────────────────────
  const calculatePosition = () => {
    const el = wrapRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    setPosition({ top: rect.bottom, left: rect.left, width: rect.width });
  };

  useEffect(() => {
    if (!isOpen) return;
    calculatePosition();
    const onScroll = () => calculatePosition();
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onScroll);
    return () => {
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onScroll);
    };
  }, [isOpen]);

  // Reset highlight when the list changes or the picker closes.
  useEffect(() => setHighlight(-1), [query, isOpen]);

  // Keep the highlighted option scrolled into view.
  useEffect(() => {
    if (!isOpen || highlight < 0 || !dropdownRef.current) return;
    const item = dropdownRef.current.querySelector(`[data-index="${highlight}"]`);
    item?.scrollIntoView({ block: 'nearest' });
  }, [highlight, isOpen]);

  // Click-outside closes (ignoring clicks inside the portalled dropdown).
  useEffect(() => {
    if (!isOpen) return;
    const onDown = (e: MouseEvent) => {
      const target = e.target as Node;
      if (wrapRef.current?.contains(target)) return;
      if (dropdownRef.current?.contains(target)) return;
      setIsOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [isOpen]);

  const select = (categoryId: string) => {
    onChange(categoryId);
    setQuery('');
    setIsOpen(false);
    inputRef.current?.blur();
  };

  const open = () => {
    if (disabled) return;
    setQuery('');
    setIsOpen(true);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Escape') {
      if (isOpen) {
        e.preventDefault();
        // Stop the Escape from also closing a surrounding modal (FormModal /
        // ConfirmDialog listen for Escape) — the picker closes first.
        e.stopPropagation();
        setIsOpen(false);
        inputRef.current?.blur();
      }
      return;
    }
    if (e.key === 'ArrowDown' && !isOpen) {
      open();
      return;
    }
    if (!isOpen) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlight((p) => Math.min(p + 1, selectableIds.length - 1));
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlight((p) => Math.max(p - 1, 0));
        break;
      case 'Enter': {
        e.preventDefault();
        const id = selectableIds[highlight];
        if (id) select(id);
        break;
      }
      default:
        break;
    }
  };

  // ─── Dropdown content ──────────────────────────────────────────────────
  const renderTree = (nodes: TreeNode[]): React.ReactNode =>
    nodes.map((node) => {
      const indent = 8 + node.depth * 16;
      if (node.children.length > 0) {
        return (
          <div key={node.cat.id}>
            <div className={styles.header} style={{ paddingLeft: indent }} role="presentation">
              <span className={styles.optionLabel}>{node.cat.name}</span>
            </div>
            {renderTree(node.children)}
          </div>
        );
      }
      const index = selectableIds.indexOf(node.cat.id);
      const active = index === highlight;
      const selected = node.cat.id === value;
      return (
        <div
          key={node.cat.id}
          id={`${listboxId.current}-opt-${index}`}
          data-index={index}
          role="option"
          aria-selected={selected}
          className={`${styles.option} ${active ? styles.optionActive : ''}`}
          style={{ paddingLeft: indent }}
          onMouseEnter={() => setHighlight(index)}
          onClick={() => select(node.cat.id)}
        >
          <span className={styles.optionLabel}>{node.cat.name}</span>
          {selected && <Icon name="check" size={14} className={styles.checkMark} />}
        </div>
      );
    });

  const dropdown =
    isOpen && position
      ? createPortal(
          <div
            ref={dropdownRef}
            id={listboxId.current}
            role="listbox"
            aria-label={ariaLabel}
            className={styles.dropdown}
            style={{
              top: position.top,
              left: position.left,
              // Grow to fit the content (full category names, no truncation),
              // but never narrower than the input nor past the viewport edge.
              minWidth: position.width,
              maxWidth: Math.max(position.width, window.innerWidth - position.left - 8),
            }}
            // Prevent the input from blurring before the click handler fires.
            onMouseDown={(e) => e.preventDefault()}
          >
            {visibleTree.length > 0 ? (
              renderTree(visibleTree)
            ) : (
              <div className={styles.emptyMsg}>No categories found.</div>
            )}
          </div>,
          document.body,
        )
      : null;

  const wrapClass = [styles.wrap, compact ? styles.compact : '', !value ? styles.empty : '']
    .filter(Boolean)
    .join(' ');

  return (
    <div className={wrapClass} ref={wrapRef}>
      <div className={styles.inputWrap}>
        <input
          ref={inputRef}
          type="text"
          className={styles.input}
          role="combobox"
          aria-expanded={isOpen}
          aria-controls={listboxId.current}
          aria-autocomplete="list"
          aria-activedescendant={
            isOpen && highlight >= 0 ? `${listboxId.current}-opt-${highlight}` : undefined
          }
          aria-label={ariaLabel}
          autoComplete="off"
          disabled={disabled}
          placeholder={placeholder}
          value={isOpen ? query : selectedPath}
          onChange={(e) => setQuery(e.target.value)}
          onFocus={open}
          onKeyDown={handleKeyDown}
        />
        <button
          type="button"
          className={styles.toggle}
          tabIndex={-1}
          disabled={disabled}
          aria-label={isOpen ? 'Close category list' : 'Open category list'}
          onClick={() => {
            if (isOpen) {
              setIsOpen(false);
            } else {
              inputRef.current?.focus();
              open();
            }
          }}
        >
          <Icon
            name="chevronDown"
            size={16}
            className={`${styles.chevron} ${isOpen ? styles.chevronOpen : ''}`}
          />
        </button>
      </div>
      {dropdown}
    </div>
  );
}
